// Code 128 (ISO/IEC 15417) for labels drawn by the browser (A4 sheets, thermal printers set up
// as normal printers); ZPL printers draw it themselves with ^BC. Code set B carries every
// printable ASCII character; runs of four or more digits switch to set C (two digits a symbol).

/** Bar/space widths of the 107 symbols: values 0–102, start A/B/C (103–105) and stop (106). */
const PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
] as const;

const START_B = 104;
const START_C = 105;
const CODE_B = 100;
const CODE_C = 99;
const STOP = 106;

/**
 * Symbol values of a barcode: start, data, check symbol, stop. Digits go two to a symbol (set C)
 * unless set B is asked for: one symbol each, as a ZPL "^FD>:" prints them.
 */
export function code128Values(text: string, only?: 'B'): number[] {
  if (text.length === 0) throw new RangeError('empty Code 128 text');
  for (const ch of text) {
    const c = ch.charCodeAt(0);
    if (c < 32 || c > 126) throw new RangeError(`not printable in Code 128 set B: ${JSON.stringify(ch)}`);
  }
  const digitsFrom = (i: number) => {
    let n = 0;
    while (i + n < text.length && text.charCodeAt(i + n) >= 48 && text.charCodeAt(i + n) <= 57) n++;
    return n;
  };
  if (only === 'B') {
    const values = [START_B, ...[...text].map((ch) => ch.charCodeAt(0) - 32)];
    return [...values, values.reduce((sum, v, k) => sum + v * (k === 0 ? 1 : k), 0) % 103, STOP];
  }
  const values: number[] = [];
  let set: 'B' | 'C';
  let i = 0;
  const lead = digitsFrom(0);
  if (lead >= 4 || (lead === text.length && lead % 2 === 0 && lead >= 2)) {
    set = 'C';
    values.push(START_C);
  } else {
    set = 'B';
    values.push(START_B);
  }
  while (i < text.length) {
    const run = digitsFrom(i);
    if (set === 'C') {
      if (run >= 2) {
        values.push(Number(text.slice(i, i + 2)));
        i += 2;
        continue;
      }
      values.push(CODE_B);
      set = 'B';
      continue;
    }
    // in set B: four digits or more are cheaper in set C (an odd one out goes first, in B)
    if (run >= 4) {
      if (run % 2 === 1) {
        values.push(text.charCodeAt(i) - 32);
        i++;
      }
      values.push(CODE_C);
      set = 'C';
      continue;
    }
    values.push(text.charCodeAt(i) - 32);
    i++;
  }
  const check = values.reduce((sum, v, k) => sum + v * (k === 0 ? 1 : k), 0) % 103;
  return [...values, check, STOP];
}

/** The barcode as modules: "1" bar, "0" space, quiet zones not included. */
export function code128Modules(text: string, only?: 'B'): string {
  return code128Values(text, only)
    .map((v) =>
      [...PATTERNS[v]!]
        .map((w, k) => (k % 2 === 0 ? '1' : '0').repeat(Number(w)))
        .join(''),
    )
    .join('');
}
